import { useState, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { parseImageReferences, findImageByReferenceName, getImageReferenceName } from '../utils/imageMentions'
import PDFPreviewModal from './PDFPreviewModal'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import './MarkdownWithImageReferences.css'

function MarkdownWithImageReferences ({ content, images = [] }) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [currentPdf, setCurrentPdf] = useState(null)

  // Ensure images have referenceName and create normalized array
  // WICHTIG: referenceName muss für Bilder UND Videos vorhanden sein
  const normalizedImages = images.map(img => {
    if (!img.referenceName) {
      // Verwende filename bevorzugt vor name (konsistent mit ChatMessage.js)
      const filename = img.filename || img.name || ''
      return {
        ...img,
        referenceName: getImageReferenceName(filename)
      }
    }
    return img
  })

  // Open lightbox at specific image index
  const openImageAt = useCallback((imageIndex) => {
    if (normalizedImages.length > 0 && imageIndex >= 0 && imageIndex < normalizedImages.length) {
      setCurrentImageIndex(imageIndex)
      setLightboxOpen(true)
    }
  }, [normalizedImages.length])

  // Extract storage path from Firebase Storage URL
  const extractStoragePathFromUrl = useCallback((url) => {
    if (!url) return null
    try {
      const match = url.match(/\/o\/([^?]+)/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
      const match2 = url.match(/storage\.googleapis\.com\/[^/]+\/(.+?)(?:\?|$)/)
      if (match2 && match2[1]) {
        return decodeURIComponent(match2[1])
      }
    } catch (e) {
      console.error('Error extracting storage path from URL:', e)
    }
    return null
  }, [])

  // Handle click on image reference
  const handleImageReferenceClick = useCallback((e, referenceName) => {
    e.preventDefault()

    // Find the image by reference name (case-insensitive)
    const image = findImageByReferenceName(normalizedImages, referenceName)

    if (image) {
      // Check if it's a PDF
      const isPDF = image.mediaType === 'pdf' || 
                    (image.filename || image.name || '').match(/\.pdf$/i) ||
                    (image.url || '').match(/\.pdf$/i)
      
      if (isPDF) {
        // Open PDF in PDFPreviewModal
        setCurrentPdf({
          filePath: extractStoragePathFromUrl(image.url),
          fileName: image.filename || image.name || 'document.pdf',
          pageNumber: 1
        })
        setPdfModalOpen(true)
      } else {
        // Open image/video in lightbox
        const imageIndex = normalizedImages.findIndex(img => {
          const imgRefName = img.referenceName || getImageReferenceName(img.filename || img.name || '')
          return imgRefName.toLowerCase() === referenceName.toLowerCase()
        })

        if (imageIndex !== -1) {
          openImageAt(imageIndex)
        }
      }
    }
  }, [normalizedImages, openImageAt, extractStoragePathFromUrl])

  // Handle empty content
  if (!content || typeof content !== 'string') {
    return null
  }

  // Pre-process content: Convert @mentions to markdown links BEFORE ReactMarkdown
  const preprocessContent = (text) => {
    const segments = parseImageReferences(text)

    let result = ''
    segments.forEach(segment => {
      if (segment.type === 'text') {
        result += segment.content
      } else if (segment.type === 'mention') {
        // Check if image exists
        const image = findImageByReferenceName(normalizedImages, segment.referenceName)

        if (image) {
          // Check if it's a PDF for different styling
          const isPDF = image.mediaType === 'pdf' || 
                       (image.filename || image.name || '').match(/\.pdf$/i)
          
          // Convert to a special markdown link that we can intercept
          // For PDFs, we'll add an icon in the renderer
          result += `[${segment.content}](#image-ref:${segment.referenceName})`
        } else {
          // Invalid reference - keep as plain text
          result += segment.content
        }
      }
    })

    return result
  }

  const processedContent = preprocessContent(content)

  return (
    <div className='markdown-with-image-references'>
      <ReactMarkdown
        components={{
          // Custom link renderer to intercept our special image reference links
          a: ({ node, href, children, ...props }) => {
            // Check if this is an image reference link
            if (href && href.startsWith('#image-ref:')) {
              const referenceName = href.substring('#image-ref:'.length)
              
              // Check if it's a PDF
              const image = findImageByReferenceName(normalizedImages, referenceName)
              const isPDF = image && (image.mediaType === 'pdf' || 
                                      (image.filename || image.name || '').match(/\.pdf$/i))

              return (
                <button
                  type='button'
                  className='markdown-image-reference-link'
                  onClick={(e) => handleImageReferenceClick(e, referenceName)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleImageReferenceClick(e, referenceName)
                    }
                  }}
                  aria-label={isPDF ? `PDF "${referenceName}" öffnen` : `Bild "${referenceName}" anzeigen`}
                  title={isPDF ? `PDF "${referenceName}" öffnen` : `Bild "${referenceName}" anzeigen`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  {isPDF && <PictureAsPdf style={{ fontSize: '14px' }} />}
                  {children}
                </button>
              )
            }

            // Regular link
            return <a href={href} {...props}>{children}</a>
          },
          // Custom Rendering für bessere Formatierung
          h1: ({ children }) => <h2 className='md-h1'>{children}</h2>,
          h2: ({ children }) => <h3 className='md-h2'>{children}</h3>,
          h3: ({ children }) => <h4 className='md-h3'>{children}</h4>,
          ul: ({ children }) => <ul className='md-list'>{children}</ul>,
          ol: ({ children }) => (
            <ol className='md-list-ordered'>{children}</ol>
          ),
          li: ({ children }) => (
            <li className='md-list-item'>{children}</li>
          ),
          p: ({ children }) => <p className='md-paragraph'>{children}</p>,
          strong: ({ children }) => (
            <strong className='md-strong'>{children}</strong>
          ),
          code: ({ inline, children }) =>
            inline ? (
              <code className='md-code-inline'>{children}</code>
            ) : (
              <pre className='md-code-block'>
                <code>{children}</code>
              </pre>
            ),
          blockquote: ({ children }) => (
            <blockquote className='md-blockquote'>{children}</blockquote>
          )
        }}
      >
        {processedContent}
      </ReactMarkdown>

      {/* Lightbox for images */}
      {normalizedImages.length > 0 && lightboxOpen && (
        <div className='markdown-image-lightbox-overlay' onClick={() => setLightboxOpen(false)}>
          <div
            className='markdown-image-lightbox-content'
            onClick={e => e.stopPropagation()}
          >
            <button
              className='markdown-image-lightbox-close'
              onClick={() => setLightboxOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setLightboxOpen(false)
                }
              }}
              aria-label='Lightbox schließen'
            >
              ✕
            </button>

            {/* Render video or image based on mediaType (PDFs are handled separately) */}
            {normalizedImages[currentImageIndex]?.url && (
              <>
                {(normalizedImages[currentImageIndex].mediaType === 'video' ||
                  (normalizedImages[currentImageIndex].url || '').match(/\.(mp4|webm|mov)$/i) ||
                  (normalizedImages[currentImageIndex].filename || '').match(/\.(mp4|webm|mov)$/i)) ? (
                  <video
                    src={normalizedImages[currentImageIndex].url}
                    controls
                    style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                    onError={(e) => {
                      // Fallback if video fails to load
                      e.target.style.display = 'none'
                    }}
                  />
                ) : (
                  <img
                    src={normalizedImages[currentImageIndex].url}
                    alt={normalizedImages[currentImageIndex]?.referenceName || `Image ${currentImageIndex + 1}`}
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.target.style.display = 'none'
                    }}
                  />
                )}
              </>
            )}

            <div className='markdown-image-lightbox-info'>
              {normalizedImages[currentImageIndex]?.referenceName && (
                <span className='markdown-image-lightbox-name'>
                  {normalizedImages[currentImageIndex].referenceName}
                </span>
              )}
              <span className='markdown-image-lightbox-counter'>
                {currentImageIndex + 1} / {normalizedImages.length}
              </span>
            </div>

            {normalizedImages.length > 1 && (
              <>
                <button
                  className='markdown-image-lightbox-prev'
                  onClick={() => setCurrentImageIndex(prev => (prev - 1 + normalizedImages.length) % normalizedImages.length)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setCurrentImageIndex(prev => (prev - 1 + normalizedImages.length) % normalizedImages.length)
                    }
                  }}
                  aria-label='Vorheriges Bild'
                >
                  ‹
                </button>
                <button
                  className='markdown-image-lightbox-next'
                  onClick={() => setCurrentImageIndex(prev => (prev + 1) % normalizedImages.length)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setCurrentImageIndex(prev => (prev + 1) % normalizedImages.length)
                    }
                  }}
                  aria-label='Nächstes Bild'
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfModalOpen && currentPdf && (
        <PDFPreviewModal
          source={currentPdf}
          onClose={() => {
            setPdfModalOpen(false)
            setCurrentPdf(null)
          }}
        />
      )}
    </div>
  )
}

export default MarkdownWithImageReferences
